import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '../ui-pages.css';
import { SkelTable } from '../components/Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Blue-accented priority tones (matching the Orders table look).
const LEVEL = {
  high:   { label: 'High',   color: '#B23A3A', bg: '#F6E4E1' },
  medium: { label: 'Medium', color: '#8A5A12', bg: '#F5EAD5' },
  low:    { label: 'Low',    color: '#2A5B8A', bg: '#E1ECF5' },
};
const lv = (n) => LEVEL[n] || LEVEL.low;

const Badge = ({ color, bg, children }) => (
  <span className="ntf-badge" style={{ color, background: bg }}>
    <span className="ntf-badge-dot" style={{ background: color }} />
    {children}
  </span>
);

const Notifications = () => {
  const { workspaceUuid } = useParams();
  const [notifications, setNotifications] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  // Opening the page clears the unread badge (server-side); the table still
  // shows what was unread on load. Emits countsChanged so sidebars update live.
  useEffect(() => {
    const t = setTimeout(() => {
      axios.patch(`${API_URL}/api/notifications/mark-all-read`, { workspaceUuid }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications?workspaceUuid=${workspaceUuid}`);
      setNotifications(res.data.notifications || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try {
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      await axios.patch(`${API_URL}/api/notifications/${id}/read`);
    } catch (err) { console.error(err); fetchNotifications(); }
  };

  const markAllAsRead = async () => {
    try {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      await axios.patch(`${API_URL}/api/notifications/mark-all-read`, { workspaceUuid });
    } catch (err) { console.error(err); fetchNotifications(); }
  };

  const handleOpenChat = (e, notif) => {
    e.stopPropagation();
    markAsRead(notif._id);
    if (notif.associatedPhone) {
      const platform = notif.platform || 'whatsapp';
      window.open(`/workspace/${workspaceUuid}/chats/${platform}/${encodeURIComponent(notif.associatedPhone)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchDeleted = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications?workspaceUuid=${workspaceUuid}&deleted=true`);
      setDeletedItems(res.data.notifications || []);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this notification? It will be moved to the Deleted tab.')) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setNotifications(prev => prev.filter(n => n._id !== id));
    try { await axios.delete(`${API_URL}/api/notifications/${id}`, { data: { deletedBy: user.email, deletedByName: user.name } }); }
    catch (err) { console.error(err); fetchNotifications(); }
  };

  const selectTab = (k) => { setFilter(k); if (k === 'deleted') fetchDeleted(); };

  const when = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const counts = {
    all: notifications.length,
    unread: unreadCount,
    high: notifications.filter(n => n.criticalLevel === 'high').length,
    medium: notifications.filter(n => n.criticalLevel === 'medium').length,
    low: notifications.filter(n => n.criticalLevel === 'low' || !n.criticalLevel).length,
  };
  const isDeletedTab = filter === 'deleted';
  const shown = isDeletedTab ? deletedItems : notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.isRead;
    return (n.criticalLevel || 'low') === filter;
  });

  const tabs = [
    { k: 'all', label: 'All' }, { k: 'unread', label: 'Unread' },
    { k: 'high', label: 'High' }, { k: 'medium', label: 'Medium' }, { k: 'low', label: 'Low' },
  ];

  return (
    <div className="ui-page">
      <style>{css}</style>
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Notifications</h1>
          <p className="ui-sub">Real-time alerts triggered by your intelligent workflows.</p>
        </div>
        {unreadCount > 0 && (
          <div className="ui-head-actions">
            <button className="ntf-action" onClick={markAllAsRead}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>
              Mark all read
            </button>
          </div>
        )}
      </header>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div className="ntf-tabs" style={{ marginBottom: 0 }}>
          {tabs.map(t => (
            <button key={t.k} className={`ntf-tab ${filter === t.k ? 'on' : ''}`} onClick={() => selectTab(t.k)}>
              {t.label}<span className="ntf-tab-n">{counts[t.k]}</span>
            </button>
          ))}
        </div>
        <button onClick={() => selectTab('deleted')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px', borderRadius: 999, fontWeight: 800, fontSize: 13, cursor: 'pointer', border: '1px solid #B23A3A', background: isDeletedTab ? '#F6E4E1' : '#fff', color: '#B23A3A' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
          Deleted{isDeletedTab ? ` (${deletedItems.length})` : ''}
        </button>
      </div>

      {loading ? (
        <SkelTable rows={8} cols={4} />
      ) : shown.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg></div>
          <h2>{filter === 'unread' ? "You're all caught up" : 'No notifications'}</h2>
          <p>System alerts from your workflows will appear here automatically.</p>
        </div>
      ) : (
        <div className="ntf-table-card">
          <div className="ntf-scroll">
            <table className="ntf-table">
              <thead>
                <tr>
                  <th>Notification</th><th>Priority</th><th>Date</th><th></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((n) => {
                  const L = lv(n.criticalLevel);
                  return (
                    <tr
                      key={n._id}
                      className={n.isRead ? '' : 'unread'}
                      onClick={() => !n.isRead && markAsRead(n._id)}
                    >
                      <td>
                        <div className="ntf-cell">
                          {!n.isRead && <span className="ntf-newdot" />}
                          <div style={{ minWidth: 0 }}>
                            <div className="ntf-cat">{n.category}</div>
                            <div className="ntf-ctx">{n.context}</div>
                          </div>
                        </div>
                      </td>
                      <td><Badge color={L.color} bg={L.bg}>{L.label}</Badge></td>
                      <td className="ntf-muted">{when(n.createdAt)}</td>
                      <td className="ntf-right">
                        {isDeletedTab ? (
                          <span style={{ fontSize: 11.5, color: '#9C978C', fontWeight: 600 }}>
                            Deleted by <b style={{ color: '#6B6862' }}>{n.deletedByName || n.deletedBy || 'Unknown'}</b>{n.deletedAt ? ` · ${when(n.deletedAt)}` : ''}
                          </span>
                        ) : (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            {n.associatedPhone && (
                              <button className="ntf-open" onClick={(e) => handleOpenChat(e, n)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                Open chat
                              </button>
                            )}
                            <button title="Delete" onClick={(e) => handleDelete(e, n._id)}
                              style={{ display: 'inline-grid', placeItems: 'center', width: 30, height: 30, background: '#fff', border: '1px solid #B23A3A', color: '#B23A3A', borderRadius: 999, cursor: 'pointer' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 5v6m4-6v6" /></svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const css = `
.ntf-action{ display:inline-flex; align-items:center; gap:8px; background:#111111; color:#F9F4EE; border:none; padding:9px 16px; border-radius:999px; font-weight:700; font-size:13px; cursor:pointer; box-shadow:4px 4px 0 rgba(17,17,17,.2); transition:transform .1s; }
.ntf-action:hover{ background:#111111; }
.ntf-action:active{ transform:translateY(1px); }

.ntf-tabs{ display:flex; gap:8px; margin-bottom:18px; max-width:100%; flex-wrap:wrap; align-items:center; }
.ntf-tab{ background:#fff; border:1px solid #E7E1D6; cursor:pointer; font-size:13px; font-weight:500; color:#3A3A37; padding:8px 15px; border-radius:999px; display:flex; align-items:center; gap:8px; white-space:nowrap; transition:color .15s, background .15s, border-color .15s; }
.ntf-tab:hover{ color:#111111; background:#FAF7F1; }
.ntf-tab.on{ background:#111111; color:#F9F4EE; border-color:#111111; box-shadow:none; }
.ntf-tab-n{ font-size:11px; font-weight:500; font-family:'IBM Plex Mono',monospace; background:#FAF7F1; color:#9C978C; border-radius:999px; padding:1px 8px; }
.ntf-tab.on .ntf-tab-n{ background:rgba(249,244,238,0.2); color:#F9F4EE; }

.ntf-table-card{ background:#fff; border:1px solid #E7E1D6; border-radius:14px; box-shadow:none; overflow:hidden; }
.ntf-scroll{ overflow-x:auto; }
.ntf-table{ width:100%; border-collapse:collapse; min-width:680px; }
.ntf-table thead th{ text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#9C978C; padding:13px 18px; background:#FAF7F1; border-bottom:1px solid #E7E1D6; white-space:nowrap; }
.ntf-table tbody td{ padding:14px 18px; border-bottom:1px solid #E7E1D6; font-size:13.5px; color:#3A3A37; vertical-align:middle; }
.ntf-table tbody tr:last-child td{ border-bottom:none; }
.ntf-table tbody tr{ transition:background .12s; }
.ntf-table tbody tr:hover{ background:#FAF7F1; }
.ntf-table tbody tr.unread{ background:#FAF7F1; cursor:pointer; }
.ntf-table tbody tr.unread:hover{ background:#F1ECE2; }
.ntf-right{ text-align:right; white-space:nowrap; }
.ntf-muted{ color:#9C978C; white-space:nowrap; font-family:'IBM Plex Mono',monospace; }

.ntf-cell{ display:flex; align-items:flex-start; gap:10px; }
.ntf-newdot{ width:8px; height:8px; border-radius:50%; background:#111111; box-shadow:0 0 0 3px rgba(17,17,17,.12); flex:none; margin-top:5px; }
.ntf-cat{ font-size:14px; font-weight:750; color:#111111; letter-spacing:-0.01em; }
.ntf-ctx{ font-size:13px; color:#6B6862; line-height:1.45; margin-top:2px; max-width:620px; }

.ntf-badge{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:999px; font-size:11.5px; font-weight:700; white-space:nowrap; }
.ntf-badge-dot{ width:6px; height:6px; border-radius:50%; flex:none; }

.ntf-open{ display:inline-flex; align-items:center; gap:6px; background:#FFFFFF; color:#3A3A37; border:1px solid #E7E1D6; padding:6px 11px; border-radius:999px; font-weight:700; font-size:12px; cursor:pointer; white-space:nowrap; transition:background .15s; }
.ntf-open:hover{ background:#111111; color:#F9F4EE; border-color:#111111; }
`;

export default Notifications;
